import { useQuery } from "@noom/hive-core";
import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

import { ShippingDetailsDocument, ShippingDetailsQuery } from "gql/graphql";

import { useAuthCredentials } from "./useAuthCredentials";

export type ShippingDetailsData = ShippingDetailsQuery["shippingDetails"];

type UseShippingDetailsResult = {
  shippingDetails: ShippingDetailsData | null;
  loading: boolean;
  error: Error | undefined;
  refetch: () => void;
};

/**
 * Loads combined shipping details for /shipping-and-refills via GraphQL
 * `shippingDetails` (address always when available; Med/OTC nextShipments).
 */
export const useShippingDetails = (): UseShippingDetailsResult => {
  const { accessCode } = useAuthCredentials();
  const [searchParams] = useSearchParams();
  const userTreatmentId = searchParams.get("userTreatmentId");

  const { data, loading: queryLoading, error, refetch } = useQuery(
    ShippingDetailsDocument,
    {
      variables: {
        accessCode: accessCode ?? "",
        userTreatmentId: userTreatmentId ?? undefined,
      },
      skip: !accessCode,
    }
  );

  const handleRefetch = useCallback(() => {
    void refetch();
  }, [refetch]);

  return {
    shippingDetails: data?.shippingDetails ?? null,
    loading: !!accessCode && queryLoading,
    error: data ? undefined : error,
    refetch: handleRefetch,
  };
};
